import { Briefcase } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { OrgCasesSummary } from '../../types/dashboard'
import { ExpandableTile, StatRow } from '../../components/dashboard'
import { formatBytes, formatPriorityLabel, formatStatusLabel, progressBarColour } from './helpers'

const PERCENT_MAX = 100
const OWNER_PREVIEW_LIMIT = 5
const TRUNCATE_OWNER_PX = 120

function CasesByStatus({ summary }: { summary: OrgCasesSummary | null }): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="space-y-1">
      <p
        className="label-mono text-xs font-medium tracking-wider uppercase"
        style={{ color: 'var(--text-muted)' }}
      >
        {t('stats.byStatus')}
      </p>
      <StatRow label={t('stats.active')} value={summary?.activeCount ?? 0} highlight />
      {summary?.byStatus &&
        Object.entries(summary.byStatus).map(([status, count]) => (
          <StatRow key={status} label={formatStatusLabel(status)} value={count} />
        ))}
    </div>
  )
}

function CasesByPriority({ summary }: { summary: OrgCasesSummary | null }): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="space-y-1">
      <p
        className="label-mono text-xs font-medium tracking-wider uppercase"
        style={{ color: 'var(--text-muted)' }}
      >
        {t('stats.byPriority')}
      </p>
      {summary?.byPriority &&
        Object.entries(summary.byPriority).map(([priority, count]) => (
          <StatRow
            key={priority}
            label={formatPriorityLabel(priority)}
            value={count}
            highlight={priority === 'critical'}
          />
        ))}
    </div>
  )
}

function CasesByOwner({ summary }: { summary: OrgCasesSummary | null }): React.ReactElement | null {
  const { t } = useTranslation('admin')
  if (!summary?.byOwner || summary.byOwner.length === 0) return null
  return (
    <div className="space-y-1 border-t pt-2" style={{ borderColor: 'var(--border)' }}>
      <p
        className="label-mono text-xs font-medium tracking-wider uppercase"
        style={{ color: 'var(--text-muted)' }}
      >
        {t('stats.byOwner')}
      </p>
      {summary.byOwner.slice(0, OWNER_PREVIEW_LIMIT).map((owner) => (
        <div key={owner.userId} className="flex items-center justify-between py-1">
          <span
            className="truncate text-sm"
            style={{ color: 'var(--text-dim)', maxWidth: `${String(TRUNCATE_OWNER_PX)}px` }}
          >
            {owner.displayName}
          </span>
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {t('stats.casesCount', { count: owner.caseCount })}
          </span>
        </div>
      ))}
    </div>
  )
}

interface ProgressBarProps {
  percent: number
}

function ProgressBar({ percent }: ProgressBarProps): React.ReactElement {
  const { t } = useTranslation('admin')
  const colour = progressBarColour(percent)
  const width = Math.min(percent, PERCENT_MAX)
  return (
    <div className="mt-2">
      <div className="mb-1 flex justify-between text-xs" style={{ color: 'var(--text-muted)' }}>
        <span>{t('stats.usage')}</span>
        <span>{String(Math.round(percent))}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full" style={{ background: 'var(--highlight)' }}>
        <div className={`h-full transition-all ${colour}`} style={{ width: `${String(width)}%` }} />
      </div>
    </div>
  )
}

interface DataVolumeStats {
  limit: number | null
  used: number
  percent: number
  usersAssigned: number
}

function deriveDataVolumeStats(summary: OrgCasesSummary | null): DataVolumeStats {
  const rawLimit = summary?.dataVolumeLimitBytes ?? null
  const used = summary?.totalDataVolumeBytes ?? 0
  const hasLimit = rawLimit !== null && rawLimit > 0
  const percent = hasLimit ? (used / rawLimit) * PERCENT_MAX : 0
  return {
    limit: rawLimit,
    used,
    percent,
    usersAssigned: summary?.uniqueUsersCount ?? 0,
  }
}

function DataVolumeBlock({ summary }: { summary: OrgCasesSummary | null }): React.ReactElement {
  const { t } = useTranslation('admin')
  const stats = deriveDataVolumeStats(summary)
  const limitLabel = stats.limit ? formatBytes(stats.limit) : t('stats.unlimited')
  const showProgress = stats.limit !== null && stats.limit > 0
  return (
    <div className="space-y-1 border-t pt-2" style={{ borderColor: 'var(--border)' }}>
      <p
        className="label-mono text-xs font-medium tracking-wider uppercase"
        style={{ color: 'var(--text-muted)' }}
      >
        {t('stats.dataVolume')}
      </p>
      <StatRow label={t('stats.used')} value={formatBytes(stats.used)} highlight />
      <StatRow label={t('stats.limit')} value={limitLabel} />
      {showProgress && <ProgressBar percent={stats.percent} />}
      <StatRow label={t('stats.usersAssigned')} value={stats.usersAssigned} />
    </div>
  )
}

export function CasesTile({ summary }: { summary: OrgCasesSummary | null }): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <ExpandableTile
      title={t('stats.cases')}
      value={summary?.totalCount ?? 0}
      icon={Briefcase}
      viewAllLink="/cases"
    >
      <div className="space-y-3">
        <CasesByStatus summary={summary} />
        <CasesByPriority summary={summary} />
        <CasesByOwner summary={summary} />
        <DataVolumeBlock summary={summary} />
      </div>
    </ExpandableTile>
  )
}
